import { RECORD_TYPE_INDICATORS, RECORD_TYPE_ORDER } from './config';
import {
  ChildrenRecords,
  RecordCountMap,
  RecordIndicator,
  RecordType
} from './types';

const KNOWN_RECORD_TYPES = new Set<RecordType>(RECORD_TYPE_ORDER);

/**
 * Collapses a backend `childrenRecords.countPerRecordType[]` array into a map
 * of counts keyed by record type, keeping only types with a count > 0.
 *
 * - Entries with count 0, missing counts, or unknown record types are ignored.
 * - Duplicate entries for the same type are summed (defensive; the backend
 *   sends one entry per type).
 * - `undefined`/empty input yields `{}` (no indicators).
 */
export const mapChildrenRecords = (
  childrenRecords?: ChildrenRecords
): RecordCountMap => {
  const entries = childrenRecords?.countPerRecordType;
  if (!Array.isArray(entries) || entries.length === 0) {
    return {};
  }

  return entries.reduce<RecordCountMap>((acc, entry) => {
    if (
      entry &&
      KNOWN_RECORD_TYPES.has(entry.recordType) &&
      typeof entry.count === 'number' &&
      entry.count > 0
    ) {
      acc[entry.recordType] = (acc[entry.recordType] ?? 0) + entry.count;
    }
    return acc;
  }, {});
};

/**
 * Builds the ordered list of indicators to render from a backend
 * `childrenRecords` payload, resolving each type's icon/label/color from the
 * config. `show` optionally restricts which record types are considered (e.g.
 * the Work Orders page shows only APPLICATION).
 */
export const buildRecordIndicators = (
  childrenRecords?: ChildrenRecords,
  show: RecordType[] = RECORD_TYPE_ORDER
): RecordIndicator[] => {
  const counts = mapChildrenRecords(childrenRecords);
  return RECORD_TYPE_ORDER.filter(
    type => show.includes(type) && (counts[type] ?? 0) > 0
  ).map(type => ({
    key: type,
    count: counts[type] as number,
    ...RECORD_TYPE_INDICATORS[type]
  }));
};

/** Display string for the counter badge: exact 1–9, "9+" above 9. */
export const formatCount = (count: number): string =>
  count > 9 ? '9+' : String(count);
